/**
 * UpgradeBanner — sticky bottom bar with progression-focused copy.
 *
 * The upper Smart Progress Banner communicates *current progress*.
 * This sticky bar communicates the *next action*, so the two never
 * repeat the same message.
 *
 * Preserves: prop signature, data-testid hooks, dismiss behavior,
 * planId branches for paid users, tel: fallback for Scale.
 */
import { Button } from "@/components/ui/button";
import { Crown, Sparkles, X, Phone, ArrowRight } from "lucide-react";

export function UpgradeBanner({ downloadsUsed, freeDownloadLimit, onUpgradeClick, onDismiss, dismissed, planId }) {
  if (dismissed) return null;

  const remaining = Math.max(0, freeDownloadLimit - downloadsUsed);
  const isFree = !planId || planId === "free";

  const getTitle = () => {
    if (isFree) {
      if (remaining === 0) return "Continue Downloading";
      if (remaining === 1) return "Last Free Sample";
      return "Explore More";
    }
    if (planId === "starter") return "You\u2019ve started upgrading your menu";
    if (planId === "growth") return "Your menu is scaling well";
    if (planId === "scale") return "You\u2019re fully upgraded";
    return `${remaining} download${remaining !== 1 ? "s" : ""} remaining`;
  };

  const getSubtitle = () => {
    if (isFree) {
      if (remaining === 0) return "Unlock Studio Pass 2.0 to keep downloading";
      if (remaining === 1) return "Verify one more dish, then unlock full access";
      return "Browse pre-approved menu photos below";
    }
    if (planId === "starter") return "Unlock premium photos with Growth or Scale Plan";
    if (planId === "growth") return "Upgrade to Scale for full access + more downloads";
    if (planId === "scale") return "Need more downloads? Contact us for bulk plans";
    return "Upgrade for more downloads";
  };

  const getCta = () => {
    if (isFree) {
      if (remaining === 0) return "Upgrade Studio Pass";
      return "Continue Browsing";
    }
    if (planId === "starter") return "Upgrade Now";
    if (planId === "growth") return "Upgrade to Scale";
    if (planId === "scale") return "Contact Us";
    return "Upgrade Now";
  };

  // Free users with samples remaining should not be pushed into the upgrade
  // modal from the sticky bar — the CTA "Continue Browsing" simply dismisses
  // the bar so they can keep exploring the photo grid.
  const isBrowseCta = isFree && remaining > 0;

  const handleCtaClick = () => {
    if (planId === "scale") {
      window.open("tel:9211622662");
      return;
    }
    if (isBrowseCta) {
      onDismiss?.();
      return;
    }
    onUpgradeClick();
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 shadow-lg">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] sm:text-sm font-semibold text-white truncate">{getTitle()}</p>
            <p className="text-[11px] sm:text-xs text-white/85 truncate">{getSubtitle()}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            onClick={handleCtaClick}
            size="sm"
            className="bg-white text-orange-600 hover:bg-white/90 font-semibold h-9 px-3.5"
            data-testid="upgrade-banner-btn"
          >
            {planId === "scale" ? (
              <Phone className="h-3.5 w-3.5 mr-1" />
            ) : isBrowseCta ? (
              <ArrowRight className="h-3.5 w-3.5 mr-1" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 mr-1" />
            )}
            {getCta()}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
            onClick={onDismiss}
            data-testid="upgrade-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
